import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lightning, Flame, ArrowRight, CaretRight, CircleNotch, WarningCircle, CheckCircle, Clock } from "@phosphor-icons/react";
import { Progress } from "@/components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MetricCard({ label, value, color, onClick }) {
  return (
    <div className={`wp-card ${onClick ? "cursor-pointer hover:border-[#0A0A0A]" : ""}`} onClick={onClick}
      data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="metric-label">{label}</div>
      <div className="metric-value" style={{ color }}>{value}</div>
    </div>
  );
}

function JobCard({ job }) {
  const isElectrical = job.trade === "electrical";
  return (
    <div
      data-testid={`job-card-${job.id}`}
      className={`wp-card ${isElectrical ? "trade-electrical" : "trade-hvac"} animate-slide-up`}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="text-sm font-bold text-[#0A0A0A]" style={{ fontFamily: "'Chivo', sans-serif" }}>
            {job.title}
          </h4>
          <p className="text-xs text-[#404040] mt-0.5">{job.address}</p>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-data rounded-sm ${
          job.status === "completed" ? "badge-complete" :
          job.status === "in_progress" ? "badge-active" : "badge-pending"
        }`}>
          {job.status === "in_progress" ? <CircleNotch size={10} className="animate-spin" /> :
           job.status === "completed" ? <CheckCircle size={10} /> : <Clock size={10} />}
          {job.status.replace("_", " ")}
        </span>
      </div>
      <div className="flex items-center gap-3 mt-2">
        <div className="flex-1">
          <Progress value={job.progress_percentage} className="h-1" />
        </div>
        <span className="font-mono-data text-[10px]">{job.progress_percentage}%</span>
      </div>
      <div className="flex items-center justify-between mt-2 text-[10px] font-mono-data text-[#404040]">
        <span>{job.actual_hours}/{job.estimated_hours} HRS</span>
        <span className="flex items-center gap-0.5">
          {isElectrical ? <Lightning size={10} /> : <Flame size={10} />}
          {job.trade.toUpperCase()}
        </span>
      </div>
    </div>
  );
}

function AlertItem({ type, message }) {
  return (
    <div className={`flex items-start gap-2 p-2 text-xs border-b border-[#E5E5E5] last:border-0`}>
      <WarningCircle size={14} className={type === "urgent" ? "text-[#FF5A00] shrink-0 mt-0.5" : "text-[#FFCC00] shrink-0 mt-0.5"} />
      <span className="text-[#404040]">{message}</span>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [jobsRes, tasksRes, invRes, maintRes] = await Promise.all([
        axios.get(`${API}/jobs`),
        axios.get(`${API}/tasks`),
        axios.get(`${API}/inventory`),
        axios.get(`${API}/maintenance`),
      ]);
      setJobs(jobsRes.data);
      setTasks(tasksRes.data);
      setInventory(invRes.data);
      setMaintenance(maintRes.data);
    } catch {
      toast.error("Failed to load dashboard data");
    }
    setLoading(false);
  }, []);

  const seedData = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/seed`);
      toast.success("Demo data loaded");
      fetchData();
    } catch {
      toast.error("Failed to seed data");
    }
    setSeeding(false);
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  const activeJobs = jobs.filter(j => j.status === "in_progress");
  const pendingTasks = tasks.filter(t => t.status === "pending");
  const urgentTasks = tasks.filter(t => t.priority === "urgent");
  const lowStock = inventory.filter(i => i.quantity <= i.min_quantity);
  const overdueMaint = maintenance.filter(m => m.status === "overdue" || m.status === "due_soon");

  const alerts = [
    ...urgentTasks.map(t => ({ type: "urgent", message: t.title })),
    ...lowStock.map(i => ({ type: "warning", message: `Low stock: ${i.name} (${i.quantity} ${i.unit} at ${i.location})` })),
    ...overdueMaint.map(m => ({ type: m.status === "overdue" ? "urgent" : "warning", message: `${m.asset_name}: ${m.notes}` })),
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <CircleNotch size={32} className="animate-spin text-[#002FA7]" />
      </div>
    );
  }

  return (
    <div className="p-4 max-w-lg mx-auto" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>
            WorkPro
          </h1>
          <p className="text-xs text-[#404040] mt-0.5" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            FIELD WORKER INTERFACE
          </p>
        </div>
        {jobs.length === 0 && (
          <button
            data-testid="seed-data-btn"
            onClick={seedData}
            disabled={seeding}
            className="px-3 py-2 bg-[#002FA7] text-white text-xs font-bold rounded-sm hover:bg-[#001D6C] transition-colors disabled:opacity-50"
          >
            {seeding ? "Loading..." : "Load Demo"}
          </button>
        )}
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-3 mb-6" data-testid="metrics-grid">
        <MetricCard label="Active Jobs" value={activeJobs.length} color="#002FA7" onClick={() => navigate("/jobs")} />
        <MetricCard label="Pending Tasks" value={pendingTasks.length} color="#FF5A00" onClick={() => navigate("/tasks")} />
        <MetricCard label="Low Stock" value={lowStock.length} color={lowStock.length > 0 ? "#FF5A00" : "#34C759"} onClick={() => navigate("/inventory")} />
        <MetricCard label="Maint. Due" value={overdueMaint.length} color={overdueMaint.length > 0 ? "#FFCC00" : "#34C759"} onClick={() => navigate("/maintenance")} />
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="mb-6" data-testid="alerts-section">
          <h2 className="text-xs font-mono-data text-[#FF5A00] mb-2 flex items-center gap-1">
            <WarningCircle size={12} />
            ALERTS ({alerts.length})
          </h2>
          <div className="wp-card p-0">
            {alerts.slice(0, 5).map((a, i) => (
              <AlertItem key={i} type={a.type} message={a.message} />
            ))}
          </div>
        </div>
      )}

      {/* Active Jobs */}
      <div className="mb-6" data-testid="active-jobs-section">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xs font-mono-data text-[#002FA7]">ACTIVE JOBS</h2>
          <button className="text-xs text-[#404040] flex items-center gap-0.5 hover:text-[#0A0A0A] transition-colors">
            All <CaretRight size={10} />
          </button>
        </div>
        {activeJobs.length === 0 ? (
          <div className="wp-card text-center text-sm text-[#404040]">
            No active jobs. Tap Jobs to create one.
          </div>
        ) : (
          <div className="space-y-3">
            {activeJobs.map((job, i) => (
              <div key={job.id} style={{ animationDelay: `${i * 0.08}s` }}>
                <JobCard job={job} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div data-testid="quick-actions">
        <h2 className="text-xs font-mono-data text-[#0A0A0A] mb-2">QUICK ACTIONS</h2>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: "New Job", color: "#002FA7", route: "/jobs" },
            { label: "Inspect Photo", color: "#FF5A00", route: "/inspect" },
            { label: "Material List", color: "#34C759", route: "/materials" },
            { label: "Customer Portal", color: "#FFCC00", route: "/customers" },
          ].map((action) => (
            <button
              key={action.label}
              data-testid={`quick-action-${action.label.toLowerCase().replace(/\s/g, '-')}`}
              onClick={() => navigate(action.route)}
              className="wp-card flex items-center gap-2 hover:border-[#0A0A0A] transition-colors cursor-pointer"
            >
              <div
                className="w-8 h-8 rounded-sm flex items-center justify-center"
                style={{ background: `${action.color}10`, color: action.color }}
              >
                <ArrowRight size={16} weight="bold" />
              </div>
              <span className="text-sm font-medium">{action.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
